import { memo } from "react";
import { View } from "@src/components";
import { StyleSheet } from "react-native-unistyles";
import Animated, { type AnimatedStyle } from "react-native-reanimated";
import { VideoControlButton } from "./VideoControlButton";
import type { ViewStyle } from "react-native";

type VideoControlsOverlayProps = {
  isPlaying: boolean;
  isEnded: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
  animatedStyle: AnimatedStyle<ViewStyle>;
  onPrevious?: () => void;
  onNext?: () => void;
  onPlayPause: () => void;
};

function getPlayButtonIcon(isPlaying: boolean, isEnded: boolean): "pause" | "play" | "refresh" {
  if (isEnded) return "refresh";
  if (isPlaying) return "pause";
  return "play";
}

export const VideoControlsOverlay = memo(
  ({
    isPlaying,
    isEnded,
    hasPrevious,
    hasNext,
    animatedStyle,
    onPrevious,
    onNext,
    onPlayPause,
  }: VideoControlsOverlayProps) => {
    return (
      // The full-cover overlay is a positioning layer only — it must let taps on
      // the empty video area fall through to the VideoPlayerView Pressable
      // beneath (tap-to-dismiss/reveal) while its button children stay clickable.
      // That is exactly `box-none` ("parent transparent to taps, children still
      // hit-testable"). Passed as a JSX PROP, not via the StyleSheet/animated
      // style: on native `box-none` is the only value that keeps the subtree
      // hit-testable (a plain "none" disables the whole subtree, so the centre
      // buttons would never receive presses); on web react-native-web only
      // honours `box-none` through the prop path (in a style it serialises to
      // invalid CSS). `animatedStyle` (opacity + auto/none pointer gating) is
      // applied to the inner button row instead, so a faded-out control is never
      // clickable (the ghost-play-button bug this originally fixed).
      <View style={styles.overlay} pointerEvents="box-none">
        <Animated.View style={[styles.container, animatedStyle]}>
          {/* Previous button */}
          <VideoControlButton icon="skipPrevious" onPress={onPrevious} disabled={!hasPrevious} />

          {/* Play/Pause/Restart button */}
          <VideoControlButton icon={getPlayButtonIcon(isPlaying, isEnded)} onPress={onPlayPause} size="large" />

          {/* Next button */}
          <VideoControlButton icon="skipNext" onPress={onNext} disabled={!hasNext} />
        </Animated.View>
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing["5xl"],
  },
}));
